/**
 * Vue directive that triggers a specified method when a click occurs outside the bound element.
 *
 * Usage in templates:
 * <div v-click-outside="methodName"></div>
 *
 * The directive attaches a click listener on the document body and calls the provided
 * method (by expression) when the click target is outside the element and its children.
 *
 * @namespace ClickOutsideDirective
 */
export default {
  /**
   * Called only once, when the directive is first bound to the element.
   * It defines an event handler on the element and registers it on the document body.
   *
   * @param {HTMLElement} el - The element the directive is bound to.
   * @param {Object} binding - An object containing the binding information provided to the directive.
   * @param {string} [binding.expression] - The string expression provided to the directive (name of the method to call).
   * @param {*} binding.value - The value passed to the directive (not used in this implementation).
   * @param {Object} vnode - The Vue virtual node. Used to access the component instance via vnode.context.
   * @returns {void}
   */
  bind: function (el, binding, vnode) {
    /**
     * Event handler invoked on body clicks. If the click occurred outside the
     * bound element, calls the configured component method using the binding expression.
     *
     * @param {MouseEvent} event - The DOM click event.
     * @returns {void}
     */
    el.clickOutsideEvent = function (event) {
      // here I check that click was outside the el and his childrens
      if (!(el == event.target || el.contains(event.target))) {
        // and if it did, call method provided in attribute value
        vnode.context[binding.expression](event);
      }
    };
    document.body.addEventListener('click', el.clickOutsideEvent);
  },

  /**
   * Called when the directive is unbound from the element. Removes the previously
   * registered click listener from the document body.
   *
   * @param {HTMLElement} el - The element the directive was bound to. The handler is stored on this element.
   * @returns {void}
   */
  unbind: function (el) {
    document.body.removeEventListener('click', el.clickOutsideEvent);
  }
};
